// DS: CR18E
// @dynamic-i18n-keys
// Dynamic t() at lines 70-73: template faveNotification.error.${wasRemoved ? 'removedFromFaves' : 'addedToFaves'}.v2.
//   faveNotification.error.removedFromFaves.v2
//   faveNotification.error.addedToFaves.v2
import React, { useContext } from 'react';
import get from 'lodash/get';
import { Trans, useTranslation } from 'react-i18next';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import { NotificationType } from '../../Notifications/redux/types';
import Notification from '../../Notifications/Notification';
import SystemNotification from '../../SystemNotification';
import { FaveNotification as FaveNotificationType } from '../../CollectionPanel/redux/types';
import AddToFaveIcon from '@wix/da-ds/pkg/Icons/24x24/AddedToFav';
import RemoveFaveIcon from '@wix/da-ds/pkg/Icons/24x24/RemovedFromFav';
import ErrorIcon from '@wix/da-ds/pkg/Icons/CloseBold';
import { getDeviationResourceType } from '../../DeviationViews/_helpers';
import { getDeviationNotificationQueue } from '../../DeviationPage/redux/helpers';
import { ActionBarContext } from '../../ActionBar/ActionBarContext';
import { BiData, PopupClickAddToCollectionBiEvent } from '@wix/da-bi/pkg/events';

type Params = FaveNotificationType['params'] & Omit<FaveNotificationType, 'params'>;

export interface Props extends Params {
  // From notification factory
  deviation: PapiDeviation;
  success: boolean;
  // From container
  currentUser: PapiUser;
  isFavourite: boolean;
  onAddToCollection: (
    deviation: PapiDeviation,
    notificationQueue: string,
    actionBarType: string
  ) => void;
  addNotification: (queueName: string, type: string | NotificationType, params: any) => void;
  removeNotification: (id: number) => void;
}

const FaveNotification: React.FC<React.PropsWithChildren<Props>> = props => {
  const {
    success,
    id,
    type,
    isFavourite,
    deviation,
    queueName,
    addNotification,
    removeNotification,
    onAddToCollection,
    currentUser,
  } = props;
  const { t } = useTranslation();
  const actionBarType = useContext(ActionBarContext);

  const deviationBiData = {
    itemid: deviation.deviationId,
    typeid: getDeviationResourceType(deviation),
    creatorid: get(deviation, 'author.useridUuid'),
  };

  const renderTitle = () => {
    const wasRemoved = isUndo() || !isFavourite;
    if (!success) {
      return t(`faveNotification.error.${wasRemoved ? 'removedFromFaves' : 'addedToFaves'}.v2`);
    }

    if (wasRemoved) {
      return (
        // prettier-ignore
        <Trans i18nKey="faveNotification.notification.removedFromFaves.v2">
          Removed from <a href={Url.userGalleryLink({
            username: currentUser.username,
            gallectionType: 'collection'
          })}>Favourites</a>
        </Trans>
      );
    }

    return (
      // prettier-ignore
      <Trans i18nKey="faveNotification.notification.addedToFaves.v2">
        Added to <a href={Url.userGalleryLink({
          username: currentUser.username,
          gallectionType: 'collection'
        })}>Favourites</a>
      </Trans>
    );
  };

  const isUndo = () => {
    return type === NotificationType.FaveUndo;
  };

  const onCollect = () => {
    onAddToCollection && onAddToCollection(deviation, queueName, actionBarType);
    removeNotification(id);
  };

  return (
    <Notification
      id={id}
      queueName={queueName}
      cardComponent={SystemNotification}
      cardProps={{
        iconComponent: success
          ? isUndo() || !isFavourite
            ? RemoveFaveIcon
            : AddToFaveIcon
          : ErrorIcon,
        backgroundDeviation: deviation,
        actions: [
          !isUndo() &&
            isFavourite && {
              type: 'positive',
              label: t('faveNotification.notification.action.addToCollection'),
              biData: BiData<PopupClickAddToCollectionBiEvent>({
                evid: 333,
                sectionname: 'notificationTooltip',
                ...deviationBiData,
              }),
              onClick: onCollect,
            },
        ],
      }}
      onClose={
        queueName === getDeviationNotificationQueue(deviation) &&
        success &&
        isFavourite &&
        !isUndo()
          ? () => addNotification(queueName, 'commentReminder', {})
          : undefined
      }
    >
      <span>{renderTitle()}</span>
    </Notification>
  );
};
FaveNotification.displayName = 'FaveNotification';

export default FaveNotification;
